import React, { useState } from "react";
import { Container, Card, Button, Spinner, Form } from "react-bootstrap";
import { FaEnvelope, FaExclamationTriangle } from "react-icons/fa";
import { useAppDispatch } from "../../redux";
import { resendVerificationEmail } from "../../redux/slices/authSlice";

const EmailVerificationRequired = ({ email, onBackToLogin }) => {
  const dispatch = useAppDispatch();
  const [isResending, setIsResending] = useState(false);
  const [inputEmail, setInputEmail] = useState(email || "");
  const [showInput, setShowInput] = useState(false);
  const [error, setError] = useState("");

  const handleResendVerification = async () => {
    if (!inputEmail.trim()) {
      setError("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsResending(true);
    setError("");
    
    try {
      const result = await dispatch(resendVerificationEmail(inputEmail)).unwrap();
      setShowInput(false);
      console.log('Verification email sent successfully:', result);
    } catch (error) {
      console.error('Error resending verification:', error);
      setError("Failed to send verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleShowInput = () => {
    setShowInput(true);
    setError("");
  };

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin();
    }
  };

  return (
    <Container
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "80vh", paddingTop: "2rem" }}
    >
      <Card
        className="p-4 shadow-lg"
        style={{ width: "100%", maxWidth: "500px" }}
      >
        <div className="text-center mb-4">
          <FaExclamationTriangle className="text-warning" size={48} />
          <h3 className="mt-3">Email Verification Required</h3>
        </div>

        <div className="text-center mb-4">
          <p className="text-muted">
            {email ? (
              <>
                We've sent a verification link to your email address:
                <br />
                <strong>{email}</strong>
                <br />
                Please check your inbox and click the verification link to activate your account.
              </>
            ) : (
              "Please enter your email address to receive a verification link."
            )}
          </p>
        </div>

        {showInput && (
          <div className="mb-4">
            <Form.Group>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email address"
                value={inputEmail}
                onChange={(e) => setInputEmail(e.target.value)}
                disabled={isResending}
                isInvalid={!!error}
              />
              {error && (
                <Form.Control.Feedback type="invalid">
                  {error}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </div>
        )}

        <div className="d-grid gap-2">
          {!showInput ? (
            <Button
              variant="primary"
              onClick={handleShowInput}
              className="mb-2"
            >
              <FaEnvelope className="me-2" />
              {email ? "Resend Verification Email" : "Send Verification Email"}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleResendVerification}
              className="mb-2"
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Sending...
                </>
              ) : (
                <>
                  <FaEnvelope className="me-2" />
                  Send Verification Email
                </>
              )}
            </Button>
          )}

          <Button 
            variant="outline-secondary" 
            onClick={handleBackToLogin}
          >
            Back To Login
          </Button>
        </div>

        <div className="text-center mt-4">
          <small className="text-muted">
            Didn't receive the email? Check your spam folder or contact support.
          </small>
        </div>
      </Card>
    </Container>
  );
};

export default EmailVerificationRequired;
